<template>
	<nuxt-link v-bind="linkAttrs">
		<slot>
			<span :class="linkClasses" class="wrapper-active">
				<UIShIcon v-if="iconLeft" :name="iconLeft" class="icon" />
				<span>{{ label }}</span>
				<UIShIcon v-if="iconRight" :name="iconRight" class="icon" />
			</span>

			<span :class="linkClasses" class="wrapper-hover">
				<UIShIcon v-if="iconLeft" :name="iconLeft" class="icon" />
				<span>{{ label }}</span>
				<UIShIcon v-if="iconRight" :name="iconRight" class="icon" />
			</span>
		</slot>
	</nuxt-link>
</template>

<script setup lang="ts">
import { type RouterLinkProps } from 'vue-router';

const LINK_SIZES = ['xs', 'sm', 'md', 'lg'] as const;
type ShLinkSizesT = 'xs' | 'sm' | 'md' | 'lg';
type ShLinkSizeName = `link-${(typeof LINK_SIZES)[number]}`;
type ShLinkSizeNameAndValue = `link-${(typeof LINK_SIZES)[number]}${number}`;
type UISizeBckPoint = ShLinkSizeName | ShLinkSizeNameAndValue;

export type ShLinkT = {
	label?: string;
	iconLeft?: string;
	iconRight?: string;
	disabled?: boolean;
	external?: boolean;
	unstyled?: boolean;
	to: RouterLinkProps['to'];
	kind?: 'gray' | 'white' | 'color';
} & { [s in ShLinkSizesT]?: number | boolean };

const props = withDefaults(defineProps<ShLinkT>(), {
	kind: 'gray'
});

const attrs = useAttrs();

const linkAttrs = computed(() => {
	const common = {
		title: props.label,
		tabIndex: 0,
		class: {
			'link-wrapper': !props.unstyled,
			'link-disabled': props.disabled
		}
	};

	if (props.external) {
		return {
			...attrs,
			...common,
			href: props.to,
			ariaLabel: props.to,
			rel: 'noopener noreferrer'
		};
	}

	return { to: props.to, ...common };
});

const linkClasses = computed(() => {
	const sizeClass = getSizeClass();

	return {
		link: true,
		[`link-${props.kind}`]: !props.disabled,
		...sizeClass
	};
});

function getSizeClass() {
	const classes: { [key in UISizeBckPoint]?: boolean } = {};

	LINK_SIZES.forEach((s) => {
		const value = props[s];

		if (!(s in props)) return;
		if (value === undefined) return;

		if (typeof value === 'boolean') {
			return (classes[`link-${s}`] = value);
		}

		classes[`link-${s}${value}`] = true;
	});

	return classes;
}
</script>

<style scoped lang="scss">
.link-wrapper {
	cursor: pointer;
	overflow: hidden;
	display: block;
	width: fit-content;
	max-width: fit-content;
	border-radius: 0.222rem;
	position: relative;

	&:focus-visible,
	&:active {
		box-shadow:
			0rem 0rem 0rem 0.111rem var(--color-white),
			0rem 0rem 0rem 0.222rem var(--color-brand500);
	}

	&:hover {
		.link.wrapper-active,
		.link.wrapper-hover {
			transform: translate3d(0, -100%, 0);
		}
	}
}

.link {
	width: fit-content;
	max-width: fit-content;

	&.wrapper-active,
	&.wrapper-hover {
		display: flex;
		align-items: center;
		justify-content: center;
		transition: transform 0.3s;
	}

	&.wrapper-hover {
		width: 100%;
		position: absolute;
		transform: translate3d(0, 0, 0);
	}
}

.link-gray {
	color: var(--color-gray300);

	&:hover {
		color: var(--color-white);
	}

	&:focus-visible,
	&:active {
		color: var(--color-gray300);
	}
}

.link-white {
	color: var(--color-white);

	&:hover {
		color: var(--color-brand600);
	}

	&:focus-visible,
	&:active {
		color: var(--color-white);
	}
}

.link-color {
	color: var(--color-brand500);

	&:hover {
		color: var(--color-brand600);
	}

	&:focus-visible,
	&:active {
		color: var(--color-brand500);
	}
}

.link-disabled {
	user-select: none;
	pointer-events: none;
	color: var(--color-gray500);

	&:focus-visible,
	&:active {
		box-shadow: none;
	}
}

$sizes: (
	xs: txsm,
	sm: tsmm,
	md: tmdm,
	lg: tlgm
);
$screens: 768, 1024, 1240, 1440, 1920;

@mixin useLink($size, $font) {
	gap: var(--link-#{$size}-gap);
	@include useFont($font);

	.icon {
		--icon-width: var(--link-#{$size}-icon-w);
		--icon-height: var(--link-#{$size}-icon-h);
	}
}

@each $size, $font in $sizes {
	.link-#{$size} {
		@include useLink($size, $font);
	}
}

@each $screen in $screens {
	@each $size, $font in $sizes {
		.link-#{$size}#{$screen} {
			@media screen and (min-width: #{$screen}px) {
				@include useLink($size, $font);
			}
		}
	}
}
</style>
